import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  AlignHorizontalSpaceAround,
  ArrowLeft,
  Boxes,
  Box,
  MousePointer2,
  Pencil,
  Redo2,
  Trash2,
  Undo2,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Skeleton,
  cn,
  useToast,
} from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { warehouseApi, type LayoutDto } from '../../warehouse/api/warehouse.api.js';
import { useWarehouseLayout, warehouseKeys } from '../../warehouse/model/warehouse.queries.js';
import { rackHeightM, rackWidthM } from '@eombor/contracts';
import { applyPlan } from '../model/apply-plan.js';
import { placeInZone, rackWithinZone } from '../model/plan-geometry.js';
import {
  draftCellCount,
  planDiff,
  useBuilderStore,
  type BuilderSelection,
  type DraftRack,
  type DraftZone,
} from '../model/builder.store.js';
import { LabelPrintDialog } from '../../warehouse/ui/LabelPrintDialog.js';
import { RackDialog } from './RackDialog.js';
import { ZoneDialog } from './ZoneDialog.js';
import { PlanCanvas, type BuilderTool } from './PlanCanvas.js';
import { DEFAULT_ZONE_COLOR, type ZoneFormValues } from './ZoneEditor.js';

export interface BuilderPageProps {
  warehouseId: string;
  onBack: () => void;
  onView3d: () => void;
}

/** Layout dagi hamma katak identifikatori — yangi qo'shilganlarni topish uchun. */
/** Xulosadagi o'lchamlar — usta bilan bir xil birlikda. */
const cmOf = (metres: number): string => String(Math.round(metres * 100));

const cellIdsOf = (layout: LayoutDto | undefined): string[] =>
  layout === undefined
    ? []
    : layout.zones.flatMap((zone) => zone.racks.flatMap((rack) => rack.cells.map((cell) => cell.id)));

const TOOLS: { id: BuilderTool; label: string; icon: typeof Boxes }[] = [
  { id: 'select', label: 'builder.toolSelect', icon: MousePointer2 },
  { id: 'zone', label: 'builder.toolZone', icon: Boxes },
  { id: 'rack', label: 'builder.toolRack', icon: Box },
  { id: 'delete', label: 'builder.toolDelete', icon: Trash2 },
];

/**
 * Ombor konstruktori — skladchining asosiy ish quroli.
 *
 * Qoralama `useBuilderStore` da: hech bir sudrash serverga so'rov yubormaydi.
 * "Saqlash" bosilganda avval **oldindan ko'rish** chiqadi (nechta zona,
 * stellaj va katak yaratiladi/o'chiriladi), chunki stellajni kichraytirish
 * kataklarni o'chiradi va bu qaytarib bo'lmaydigan amal.
 */
export const BuilderPage = ({ warehouseId, onBack, onView3d }: BuilderPageProps) => {
  const { can } = usePermission();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [tool, setTool] = useState<BuilderTool>('select');
  const [previewOpen, setPreviewOpen] = useState(false);
  /* Saqlashdan keyin YANGI yaratilgan kataklar. Etiketka oynasi shular bilan
     ochiladi: yangi javon qurilgach navbatdagi ish — kodni bosib chiqarib
     ramaga yopishtirish, aks holda ekrandagi kod bilan javondagi javon
     bog'lanmay qoladi. */
  const [freshCellIds, setFreshCellIds] = useState<string[]>([]);
  /* Stellaj ustasi. `null` — yopiq; satr — o'sha zona oldindan tanlangan. */
  const [newRackZoneId, setNewRackZoneId] = useState<string | null | undefined>(undefined);
  const [newZoneOpen, setNewZoneOpen] = useState(false);
  /* Tahrirlash — o'sha oynalar, faqat mavjud yozuv bilan. */
  const [editRackId, setEditRackId] = useState<string | null>(null);
  const [editZoneId, setEditZoneId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<BuilderSelection | null>(null);

  const layout = useWarehouseLayout(warehouseId);

  const zones = useBuilderStore((state) => state.zones);
  const racks = useBuilderStore((state) => state.racks);
  const dirty = useBuilderStore((state) => state.dirty);
  const floor = useBuilderStore((state) => state.floor);
  const baseline = useBuilderStore((state) => state.baseline);
  const selected = useBuilderStore((state) => state.selected);
  const canUndo = useBuilderStore((state) => state.past.length > 0);
  const canRedo = useBuilderStore((state) => state.future.length > 0);
  const store = useBuilderStore.getState;

  /* Qoralama serverdan kelgan holat bilan bir marta to'ldiriladi. Keyin
     `layout` qayta so'ralsa ham qoralama ustiga yozilmaydi — odamning
     chizmagani yo'qolmasligi kerak. */
  useEffect(() => {
    if (layout.data === undefined) return;
    if (useBuilderStore.getState().warehouseId === layout.data.warehouse.id) return;
    useBuilderStore.getState().loadFromLayout(layout.data);
  }, [layout.data]);

  const warehouse = layout.data?.warehouse;
  const allowed = can('warehouse.layout.edit', {
    warehouseId,
    ...(warehouse === undefined ? {} : { mtuId: warehouse.mtuId, orgUnitId: warehouse.orgUnitId }),
  });

  const canPrint = can('warehouse.label.print', {
    warehouseId,
    ...(warehouse === undefined ? {} : { mtuId: warehouse.mtuId, orgUnitId: warehouse.orgUnitId }),
  });

  const diff = useMemo(() => planDiff(baseline, { zones, racks }), [baseline, zones, racks]);

  /** Tanlangan javon o'z zonasidan chiqib ketganmi — tuzatish tugmasi uchun. */
  const escapedRack = useMemo(() => {
    const rack = selected?.kind === 'rack' ? racks.find((candidate) => candidate.id === selected.id) : undefined;
    if (rack === undefined) return null;
    const zone = zones.find((candidate) => candidate.id === rack.zoneId);
    if (zone === undefined || rackWithinZone(rack, zone.plan)) return null;
    return { rack, zone };
  }, [racks, zones, selected]);

  const selectedZone = useMemo<DraftZone | null>(
    () => (selected?.kind === 'zone' ? zones.find((zone) => zone.id === selected.id) ?? null : null),
    [zones, selected],
  );
  const selectedRack = useMemo<DraftRack | null>(
    () => (selected?.kind === 'rack' ? racks.find((rack) => rack.id === selected.id) ?? null : null),
    [racks, selected],
  );
  /*
   * O'chirish nishoni TURI bilan izlanadi. Ilgari faqat `id` bo'yicha
   * izlanardi va `rack` birinchi tekshirilardi: zonani o'chirishga bergan
   * buyruq o'sha raqamli STELLAJNI o'chirib yuborardi (bazada `zone 1` va
   * `rack 1` bir vaqtda bo'ladi).
   */
  const deleteTarget = useMemo(
    () => ({
      zone:
        pendingDelete?.kind === 'zone'
          ? zones.find((zone) => zone.id === pendingDelete.id) ?? null
          : null,
      rack:
        pendingDelete?.kind === 'rack'
          ? racks.find((rack) => rack.id === pendingDelete.id) ?? null
          : null,
    }),
    [zones, racks, pendingDelete],
  );

  /*
   * ICHIDA MAHSULOT BOR javon o'chirilmaydi va buni HOZIR aytish kerak.
   *
   * Server baribir 409 beradi, lekin reja ketma-ket va tranzaksiyasiz
   * yoziladi: rad javobi saqlash o'rtasida kelsa, undan oldingi
   * o'zgarishlar allaqachon yozilgan bo'ladi va odam yarim saqlangan
   * reja bilan qoladi. Savolni o'chirish bosilganda berish buni
   * butunlay oldini oladi.
   */
  const blockedRack = (deleteTarget.rack?.occupiedCells ?? 0) > 0;

  const save = useMutation({
    mutationFn: async (): Promise<{ before: Set<string>; fresh: LayoutDto | null }> => {
      // Saqlashdan OLDINGI kataklar: farqi aynan yangi yaratilganlar bo'ladi.
      const before = new Set(cellIdsOf(layout.data));
      await applyPlan(warehouseApi, warehouseId, baseline, { zones, racks });

      /*
       * SHU YERDAN KEYIN REJA ALLAQACHON SAQLANGAN.
       *
       * Qolgani — ro'yxatni qayta o'qish — ikkinchi darajali ish va
       * uning nosozligi «saqlanmadi» degani EMAS. Ilgari u shu
       * funksiyadan tashqariga tashlanib, `onError` ga tushardi:
       * server hamma o'zgarishni qabul qilgani holda odam «Serverga
       * ulanib bo'lmadi» xabarini ko'rardi va rejani ikkinchi marta
       * saqlashga urinardi.
       */
      const fresh = await warehouseApi.layout(warehouseId).catch(() => null);
      return { before, fresh };
    },
    onSuccess: async ({ before, fresh }) => {
      setPreviewOpen(false);
      // Boshqa ekranlarning keshi — bu ham ikkinchi darajali.
      await queryClient.invalidateQueries({ queryKey: warehouseKeys.all }).catch(() => undefined);

      /* Qayta o'qish bo'lmadi: saqlangani ROST, lekin ekrandagi reja
         endi serverdagidan orqada. Buni aytmasak, odam eski qoralama
         ustida ishlashda davom etardi. */
      if (fresh === null) {
        toast.show(t('builder.savedStale'), 'warn');
        return;
      }

      useBuilderStore.getState().loadFromLayout(fresh);
      toast.show(t('builder.saved'), 'success');

      const created = cellIdsOf(fresh).filter((id) => !before.has(id));
      if (created.length > 0 && canPrint) setFreshCellIds(created);
    },
    onError: (error: unknown) => {
      toast.show(
        error instanceof ApiClientError ? error.message : t('builder.saveFailed'),
        'danger',
      );
    },
  });

  /* `⌘Z` / `⇧⌘Z` — hujjat darajasida: fokus qaysi maydonda turganidan qat'i
     nazar ishlaydi, lekin matn maydonining o'z bekor qilishiga tegmaydi. */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') return;
      const target = event.target;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return;
      event.preventDefault();
      if (event.shiftKey) useBuilderStore.getState().redo();
      else useBuilderStore.getState().undo();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const rejected = (): void => toast.show(t('builder.rejected'), 'danger');



  if (!allowed && layout.data !== undefined) {
    return (
      <Card>
        <EmptyState title={t('builder.forbidden')} description={t('builder.forbiddenHint')} />
      </Card>
    );
  }

  if (layout.isPending) return <Skeleton className="h-[36rem] w-full" />;

  if (layout.isError) {
    return (
      <Card>
        <EmptyState
          title={t('warehouse.loadError')}
          description={layout.error instanceof ApiClientError ? layout.error.message : undefined}
          action={
            <Button variant="secondary" onClick={() => void layout.refetch()}>
              {t('warehouse.retry')}
            </Button>
          }
        />
      </Card>
    );
  }

  const nothingToDo =
    diff.zonesToCreate.length === 0 &&
    diff.zonesToUpdate.length === 0 &&
    diff.zonesToDelete.length === 0 &&
    diff.racksToCreate.length === 0 &&
    diff.racksToUpdate.length === 0 &&
    diff.racksToDelete.length === 0;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <Button variant="ghost" size="sm" onClick={onBack} className="-ml-3 self-start">
            <ArrowLeft size={14} aria-hidden />
            {t('builder.back')}
          </Button>
          <h1 className="min-w-0 truncate text-xl font-semibold tracking-tight text-text">
            {t('builder.title')}
          </h1>
          <p className="max-w-prose text-sm text-text-muted">{t('builder.subtitle')}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {dirty ? (
            <Badge tone="warn">
              <span
                aria-hidden
                className="mr-1.5 inline-block size-1.5 rounded-[var(--radius-pill)] bg-warn"
              />
              {t('builder.dirty')}
            </Badge>
          ) : null}

          <Button
            variant="secondary"
            size="sm"
            disabled={!canUndo}
            onClick={() => store().undo()}
            title="⌘Z"
          >
            <Undo2 size={14} aria-hidden />
            {t('builder.undo')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!canRedo}
            onClick={() => store().redo()}
            title="⇧⌘Z"
          >
            <Redo2 size={14} aria-hidden />
            {t('builder.redo')}
          </Button>
          <Button variant="ghost" size="sm" disabled={!dirty} onClick={() => store().reset()}>
            {t('builder.reset')}
          </Button>
          <Button variant="secondary" size="sm" onClick={onView3d}>
            {t('builder.view3d')}
          </Button>
          <Button size="sm" disabled={nothingToDo} onClick={() => setPreviewOpen(true)}>
            {t('builder.save')}
          </Button>
        </div>
      </header>

      <div className="grid min-w-0 gap-4 xl:grid-cols-[18rem_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          <Card title={t('builder.tools')}>
            <ul className="flex flex-col gap-1">
              {TOOLS.map((item) => {
                const Icon = item.icon;
                const active = tool === item.id;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      aria-pressed={active}
                      onClick={() => {
                        if (item.id === 'zone') {
                          // Zona ham sudrab chizilmaydi: sichqoncha bilan
                          // chizilgan to'rtburchak baribir qo'lda tuzatilardi.
                          setNewZoneOpen(true);
                          return;
                        }
                        if (item.id === 'rack') {
                          // Javon rejaga bosib emas, ustada qo'shiladi:
                          // o'lchamsiz qo'yilgan javon rejada ma'nosiz
                          // kulrang to'rtburchak bo'lib turardi.
                          setNewRackZoneId(null);
                          return;
                        }
                        setTool(item.id);
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-[var(--radius-sm)] px-2 py-2',
                        'text-left text-sm transition-[background-color,color]',
                        'duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
                        active
                          ? 'bg-accent-soft text-accent'
                          : 'text-text-muted hover:bg-surface-2 hover:text-text',
                      )}
                    >
                      <Icon size={16} aria-hidden className="shrink-0" />
                      {t(item.label)}
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>

          {/*
            XOSSALAR PANELI — faqat XULOSA va "Tahrirlash".

            Ilgari bu yerda to'liq forma turardi va o'lchamni ikki joydan
            berish mumkin edi: shu panelda raqam bilan va rejada burchakni
            sudrab. Ikkinchisi birinchisini jimgina buzardi. Endi o'lcham
            faqat oynada beriladi — u yerda javonning 3D tasviri ham bor va
            kiritilgan raqam nimaga aylanayotgani ko'rinib turadi.
          */}
          <Card title={t('builder.properties')}>
            {selectedRack !== null ? (
              <div className="flex flex-col gap-3">
                <dl className="flex flex-col gap-2 text-sm">
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-text-faint">{t('builder.fieldRackCode')}</dt>
                    <dd className="text-text tabular">{selectedRack.code}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-text-faint">{t('builder.summarySize')}</dt>
                    <dd className="text-text tabular">
                      {`${cmOf(rackWidthM(selectedRack))} × ${cmOf(selectedRack.depthM)} × ${cmOf(rackHeightM(selectedRack))} ${t('builder.unitCm')}`}
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-text-faint">{t('builder.summaryGrid')}</dt>
                    <dd className="text-text tabular">
                      {t('builder.summaryGridValue', {
                        bays: selectedRack.bays.length,
                        levels: selectedRack.levelHeights.length,
                        cells: draftCellCount(selectedRack),
                      })}
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-text-faint">{t('builder.fieldRotation')}</dt>
                    <dd className="text-text">
                      {selectedRack.pos.rotationDeg === 0
                        ? t('builder.orientAlong')
                        : t('builder.orientAcross')}
                    </dd>
                  </div>
                </dl>

                <Button size="sm" onClick={() => setEditRackId(selectedRack.id)}>
                  <Pencil size={14} aria-hidden />
                  {t('builder.edit')}
                </Button>

                {/* Zonasidan chiqib ketgan javon — eski konstruktordan qolgan
                    holat. Uni AVTOMATIK ko'chirmaymiz (javon allaqachon
                    shunday qurilgan bo'lishi mumkin), lekin bir bosishda
                    joyiga tushiradigan yo'lni beramiz. */}
                {escapedRack === null ? null : (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const at = placeInZone(
                        escapedRack.zone.plan,
                        escapedRack.rack,
                        racks.filter((other) => other.id !== escapedRack.rack.id),
                        floor,
                        escapedRack.rack.pos.rotationDeg,
                      );
                      if (at === null) {
                        toast.show(t('builder.fitToZoneFailed'), 'danger');
                        return;
                      }
                      store().moveRack(escapedRack.rack.id, at.x, at.y);
                    }}
                  >
                    <AlertTriangle size={14} aria-hidden />
                    {t('builder.fitToZone')}
                  </Button>
                )}
              </div>
            ) : selectedZone !== null ? (
              <div className="flex flex-col gap-3">
                <dl className="flex flex-col gap-2 text-sm">
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-text-faint">{t('builder.fieldZoneCode')}</dt>
                    <dd className="text-text tabular">{selectedZone.code}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-text-faint">{t('builder.summarySize')}</dt>
                    <dd className="text-text tabular">
                      {`${selectedZone.plan.w} × ${selectedZone.plan.d} m`}
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-text-faint">{t('builder.summaryRacks')}</dt>
                    <dd className="text-text tabular">
                      {racks.filter((rack) => rack.zoneId === selectedZone.id).length}
                    </dd>
                  </div>
                </dl>

                <Button size="sm" onClick={() => setEditZoneId(selectedZone.id)}>
                  <Pencil size={14} aria-hidden />
                  {t('builder.edit')}
                </Button>

                {/* ZICHLASH — eski javonlar uchun. Yangisi allaqachon
                    qo'shnisiga tegib tushadi, lekin ilgari qo'yilganlarning
                    orasida 25 santimetrgacha tirqish qolgan va ularni qo'lda
                    surib tekislash amalda mumkin emas. */}
                {racks.filter((rack) => rack.zoneId === selectedZone.id).length < 2 ? null : (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      store().compactZone(selectedZone.id);
                      toast.show(t('builder.compactDone'), 'success');
                    }}
                  >
                    <AlignHorizontalSpaceAround size={14} aria-hidden />
                    {t('builder.compactZone')}
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-sm text-text-muted">{t('builder.nothingSelected')}</p>
            )}
          </Card>
        </div>

        <Card className="min-w-0" padded={false}>
          <div className="p-3">
            <PlanCanvas
              floor={floor}
              zones={zones}
              racks={racks}
              selected={selected}
              tool={tool}
              onSelect={(id) => store().select(id)}
              onMoveRack={(id, x, y) => store().moveRack(id, x, y)}
              onRequestDelete={setPendingDelete}
              onDrawZone={(rect) => {
                const code = String.fromCharCode(65 + zones.length);
                if (
                  store().addZone({ code, name: `${code} zona`, color: DEFAULT_ZONE_COLOR, plan: rect }) ===
                  null
                ) {
                  rejected();
                }
                setTool('select');
              }}
              onMoveZone={(id, x, y) => store().moveZone(id, x, y)}
              onAddRackAt={(x, y) => {
                // Rejaga bosish ham o'sha ustani ochadi — faqat bosilgan
                // nuqtadagi zona oldindan tanlangan bo'ladi.
                const under = zones.find(
                  (zone) =>
                    x >= zone.plan.x &&
                    x <= zone.plan.x + zone.plan.w &&
                    y >= zone.plan.y &&
                    y <= zone.plan.y + zone.plan.d,
                );
                setNewRackZoneId(under?.id ?? null);
                setTool('select');
              }}
            />
          </div>
        </Card>
      </div>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={t('builder.previewTitle')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPreviewOpen(false)}>
              {t('builder.cancel')}
            </Button>
            <Button loading={save.isPending} onClick={() => save.mutate()}>
              {t('builder.previewConfirm')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-2 text-sm text-text">
          {diff.zonesToCreate.length + diff.racksToCreate.length > 0 ? (
            <p>
              {t('builder.previewSummary', {
                zones: diff.zonesToCreate.length,
                racks: diff.racksToCreate.length,
                cells: diff.cellsToCreate,
              })}
            </p>
          ) : null}
          {diff.zonesToUpdate.length + diff.racksToUpdate.length > 0 ? (
            <p>
              {t('builder.previewUpdates', {
                zones: diff.zonesToUpdate.length,
                racks: diff.racksToUpdate.length,
              })}
            </p>
          ) : null}
          {diff.zonesToDelete.length + diff.racksToDelete.length > 0 || diff.cellsToRemove > 0 ? (
            <p className="text-warn">
              {t('builder.previewDeletes', {
                zones: diff.zonesToDelete.length,
                racks: diff.racksToDelete.length,
                cells: diff.cellsToRemove,
              })}
            </p>
          ) : null}
          <p className="font-medium">{t('builder.previewQuestion')}</p>
        </div>
      </Dialog>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete === null) return;
          // To'la javon o'chirilmaydi — oyna faqat sababni aytadi.
          if (blockedRack) {
            setPendingDelete(null);
            return;
          }
          if (deleteTarget.rack !== null) {
            store().removeRack(deleteTarget.rack.id);
          } else if (deleteTarget.zone !== null) {
            // Ichida javoni bor zona o'chirilmaydi: javonlarning
            // kataklarida qoldiq bo'lishi mumkin.
            const zoneId = deleteTarget.zone.id;
            if (racks.some((rack) => rack.zoneId === zoneId)) {
              toast.show(t('builder.deleteZoneBlocked'), 'danger');
            } else {
              store().removeZone(zoneId);
            }
          }
          setPendingDelete(null);
        }}
        tone={blockedRack ? 'warn' : 'danger'}
        title={blockedRack ? t('builder.deleteBlockedTitle') : t('builder.deleteTitle')}
        description={
          blockedRack && deleteTarget.rack !== null
            ? t('builder.deleteRackBlocked', {
                name: deleteTarget.rack.name ?? deleteTarget.rack.code,
                cells: deleteTarget.rack.occupiedCells,
              })
            : deleteTarget.rack !== null
              ? t('builder.deleteRack', {
                  name: deleteTarget.rack.name ?? deleteTarget.rack.code,
                  cells: Math.max(deleteTarget.rack.serverCells, draftCellCount(deleteTarget.rack)),
                })
              : deleteTarget.zone !== null
                ? t('builder.deleteZone', { name: deleteTarget.zone.name })
                : ''
        }
        confirmLabel={blockedRack ? t('builder.understood') : t('builder.deleteConfirm')}
        /* To'la javon oynasida tanlov YO'Q: ikkita tugma ham yopish
           bo'lardi va odam qaysi birini bosishini o'ylab qolardi. */
        cancelLabel={blockedRack ? undefined : t('builder.cancel')}
      />

      <ZoneDialog
        open={newZoneOpen || editZoneId !== null}
        onClose={() => {
          setNewZoneOpen(false);
          setEditZoneId(null);
        }}
        zones={zones}
        floor={floor}
        zone={zones.find((zone) => zone.id === editZoneId) ?? null}
        onSubmit={(values: ZoneFormValues) => {
          const plan = { x: values.x, y: values.y, w: values.w, d: values.d };
          if (editZoneId !== null) {
            store().updateZone(editZoneId, { name: values.name, color: values.color, plan });
            return;
          }
          if (
            store().addZone({
              code: values.code,
              name: values.name,
              color: values.color,
              plan,
            }) === null
          ) {
            rejected();
          }
        }}
      />

      {zones.length > 0 ? (
        <RackDialog
          open={newRackZoneId !== undefined || editRackId !== null}
          onClose={() => {
            setNewRackZoneId(undefined);
            setEditRackId(null);
          }}
          zones={zones}
          racks={racks}
          floor={floor}
          rack={racks.find((rack) => rack.id === editRackId) ?? null}
          defaultZoneId={newRackZoneId ?? null}
          onSubmit={({ zoneId, values, x, y }) => {
            const patch = {
              zoneId,
              code: values.code,
              name: values.name === '' ? null : values.name,
              kind: values.kind,
              pos: { x, y, rotationDeg: values.rotationDeg },
              depthM: values.depthM,
              /*
               * Ustada bo'linish HAMMA QATOR uchun bitta: javon shu yerda
               * yasaladi va yasashda qatorlarni bir-biridan ajratish
               * ortiqcha ish bo'lardi. Qatorni alohida bo'lish keyin,
               * katak kartochkasidan qilinadi — omborchi javon oldida
               * turib "shu taxtaga ajratgich kerak" deb qaror qiladi.
               */
              bays: values.levelHeights.map(() => [...values.bays]),
              levelHeights: values.levelHeights,
            };
            if (editRackId !== null) {
              store().updateRack(editRackId, patch);
              return;
            }
            const id = store().addRack(patch);
            if (id === null) rejected();
            else store().select({ kind: 'rack', id });
          }}
        />
      ) : null}

      {/* Yangi javon qurilgach navbatdagi ish — etiketkani bosib chiqarish. */}
      <LabelPrintDialog
        open={freshCellIds.length > 0}
        onClose={() => setFreshCellIds([])}
        cellIds={freshCellIds}
      />
    </div>
  );
};
